import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Avatar,
  Fade,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  AccountBalance as FinanceiroIcon,
  Receipt as CobrancasIcon,
  ReceiptLong as FaturasListIcon,
  Person as MinhasCobrancasIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CobrancaTable from './components/CobrancaTable';
import FinanceiroDashboardTab from './components/FinanceiroDashboardTab';
import FaturasTabContent from './components/FaturasTabContent';
import GerenciarCobrancaModal from './components/GerenciarCobrancaModal';
import { CobrancaFinanceiro } from '../../types';
import { useRelatorioCores } from '../relatorios';





interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`faturas-cobrancas-tabpanel-${index}`}
      aria-labelledby={`faturas-cobrancas-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={true} timeout={500}>
          <Box sx={{ p: { xs: 0.5, sm: 1 }, minHeight: '320px' }}>
            {children}
          </Box>
        </Fade>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `faturas-cobrancas-tab-${index}`,
    'aria-controls': `faturas-cobrancas-tabpanel-${index}`,
  };
}

const FaturasCobrancasPage: React.FC = () => {
  const theme = useTheme();
  const cores = useRelatorioCores();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [selectedCobrancaId, setSelectedCobrancaId] = useState<number | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleManageCobranca = (cobranca: CobrancaFinanceiro) => {
    setSelectedCobrancaId(cobranca.id);
    setManageModalOpen(true);
    setTabValue(3);
  };

  const handleManageCobrancaById = (id: number) => {
    setSelectedCobrancaId(id);
    setManageModalOpen(true);
  };



  const handleBack = () => {
    navigate('/dashboard');
  };

  return (
    <>
      <Box sx={{ p: 0.5 }}>
        <Box sx={{ mb: 1 }}>
          <Paper
            elevation={2}
            sx={{
              mb: 1,
              borderRadius: 2,
              overflow: 'hidden',
              background: `linear-gradient(135deg, ${alpha(cores.primario, 0.04)} 0%, ${alpha(cores.secundario, 0.04)} 100%)`,
            }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Tooltip title="Voltar">
                  <IconButton
                    size="small"
                    onClick={handleBack}
                    sx={{ border: `1px solid ${theme.palette.divider}` }}
                  >
                    <ArrowBack sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Avatar sx={{ bgcolor: alpha(cores.primario, 0.12), color: cores.primario, width: 40, height: 40 }}>
                  <FinanceiroIcon sx={{ fontSize: 22 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                    Faturas e Cobranças
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                    Gerencie faturas, cobranças e receba pagamentos de forma simplificada.
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', minHeight: 42, gap: 0.8 },
                
              }}
            >
              <Tab
                label="Faturas"
                icon={<FaturasListIcon />}
                iconPosition="start"
                {...a11yProps(0)}
              />
              <Tab
                label="Financeiro"
                icon={<FinanceiroIcon />}
                iconPosition="start"
                {...a11yProps(1)}
              />
              <Tab
                label="Cobranças"
                icon={<CobrancasIcon />}
                iconPosition="start"
                {...a11yProps(2)}
              />
              <Tab
                label="Minhas Cobranças"
                icon={<MinhasCobrancasIcon />}
                iconPosition="start"
                {...a11yProps(3)}
              />
            </Tabs>
          </Paper>
        </Box>

        {/* Abas */}
        <Paper
          elevation={0}          
          sx={{            
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
          }}
        >
          {/* Conteúdo das Abas */}
          <TabPanel value={tabValue} index={0}>
            <FaturasTabContent />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <FinanceiroDashboardTab 
              onCobrancaCreated={handleManageCobranca} 
              onManageCobranca={handleManageCobrancaById} 
            />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <CobrancaTable onManage={handleManageCobranca} />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <CobrancaTable responsavel_id={user?.id} onManage={handleManageCobranca} />
          </TabPanel>
        </Paper>

        <GerenciarCobrancaModal 
          open={manageModalOpen}
          onClose={() => setManageModalOpen(false)}
          cobrancaId={selectedCobrancaId}
          onSuccess={() => {
            // Se necessário, recarregar tabelas
          }}
        />
      </Box>

    </>
  );
};

export default FaturasCobrancasPage;
